<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type { ValidData as ReturnLabelsPrintDialogFormValidData } from '@/components/dialogForms/ReturnLabelsPrintDialogForm.vue';

import { ref, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import PrintersCustomTable from '@/components/customTables/PrintersCustomTable.vue';
import ReturnLabelsPrintDialogForm from '@/components/dialogForms/ReturnLabelsPrintDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';
import usePrintDialogData from '@/composables/data/usePrintDialogData';

export interface Props {
  isConfirmLoading?: boolean;
  drivers?: [string, number][];
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  drivers: () => [],
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: ReturnLabelsPrintDialogFormValidData,
    selectedPrinterId: number,
    props: DefProps,
  ];
}>();

const { isFetchingUserPrinters, userPrinters } = usePrintDialogData();

const selectedPrinterId = ref(NaN);

function handleSelectPrinter(printerId: number): void {
  selectedPrinterId.value = printerId;
}

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: ReturnLabelsPrintDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, selectedPrinterId.value, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}
</script>

<template>
  <PageDialogWrapper
    :class="$style.returnLabelsPrintDialog"
    data-testid="returnLabelsPrintDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Return labels: Choose a printer
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <ReturnLabelsPrintDialogForm
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
      <div class="mx-n8">
        <PrintersCustomTable
          :printers="userPrinters"
          :loading="isFetchingUserPrinters"
          @select-printer="handleSelectPrinter"
        />
      </div>
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-printer-outline"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.drivers.length || !selectedPrinterId"
            @click="handleConfirmClick"
          >
            Print
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.returnLabelsPrintDialog {
  width: 520px;
}
  .main {
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
